import { SectionCard, Field, TextInput } from "@/components/shared";
import { validateStartHereConfig } from "@/lib/startHereRules";

export function normalizeStartHere(startHere) {
  return {
    enabled: true,
    title: "Start here",
    description: "",
    questions: [],
    ...startHere,
  };
}

export function nextId(prefix, existingIds) {
  const taken = new Set(existingIds || []);
  let i = 1;
  while (taken.has(`${prefix}-${i}`)) i += 1;
  return `${prefix}-${i}`;
}

function StartHereTab({ config, onChange, products }) {
  const startHere = normalizeStartHere(config.startHere);
  const questionIds = startHere.questions.map((q) => q.id);
  const errors = validateStartHereConfig(startHere, products);

  const updateStartHere = (nextStartHere) => {
    onChange({ ...config, startHere: nextStartHere });
  };

  const updateQuestion = (questionIndex, updatedQuestion) => {
    const questions = startHere.questions.map((q, i) => (i === questionIndex ? updatedQuestion : q));
    updateStartHere({ ...startHere, questions });
  };

  const addQuestion = () => {
    const id = nextId("question", questionIds);
    const questions = [
      ...startHere.questions,
      {
        id,
        prompt: "New question",
        options: [],
      },
    ];
    updateStartHere({ ...startHere, questions });
  };

  const deleteQuestion = (questionIndex) => {
    const questions = startHere.questions.filter((_, i) => i !== questionIndex);
    updateStartHere({ ...startHere, questions });
  };

  const updateOption = (questionIndex, optionIndex, updatedOption) => {
    const question = startHere.questions[questionIndex];
    const options = question.options.map((option, i) => (i === optionIndex ? updatedOption : option));
    updateQuestion(questionIndex, { ...question, options });
  };

  const addOption = (questionIndex) => {
    const question = startHere.questions[questionIndex];
    const optionIds = question.options.map((option) => option.id);
    const option = {
      id: nextId("option", optionIds),
      label: "New option",
      weights: [],
    };
    updateQuestion(questionIndex, { ...question, options: [...question.options, option] });
  };

  const deleteOption = (questionIndex, optionIndex) => {
    const question = startHere.questions[questionIndex];
    const options = question.options.filter((_, i) => i !== optionIndex);
    updateQuestion(questionIndex, { ...question, options });
  };

  const updateWeight = (questionIndex, optionIndex, weightIndex, updatedWeight) => {
    const question = startHere.questions[questionIndex];
    const option = question.options[optionIndex];
    const weights = option.weights.map((weight, i) => (i === weightIndex ? updatedWeight : weight));
    updateOption(questionIndex, optionIndex, { ...option, weights });
  };

  const addWeight = (questionIndex, optionIndex) => {
    const question = startHere.questions[questionIndex];
    const option = question.options[optionIndex];
    const defaultProductId = products?.[0]?.id || "";
    const weights = [...option.weights, { productId: defaultProductId, score: 1 }];
    updateOption(questionIndex, optionIndex, { ...option, weights });
  };

  const deleteWeight = (questionIndex, optionIndex, weightIndex) => {
    const question = startHere.questions[questionIndex];
    const option = question.options[optionIndex];
    const weights = option.weights.filter((_, i) => i !== weightIndex);
    updateOption(questionIndex, optionIndex, { ...option, weights });
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Start Here Wizard" accentColor={config.colors.primary}>
        <label className="flex items-center gap-2 text-sm text-[#3A3530]">
          <input
            type="checkbox"
            checked={startHere.enabled !== false}
            onChange={(e) => updateStartHere({ ...startHere, enabled: e.target.checked })}
            className="accent-[#2B7D6E]"
          />
          Enable Start Here
        </label>

        <Field label="Section Title">
          <TextInput
            value={startHere.title}
            onChange={(value) => updateStartHere({ ...startHere, title: value })}
            placeholder="Start here"
          />
        </Field>

        <Field label="Description">
          <TextInput
            value={startHere.description}
            onChange={(value) => updateStartHere({ ...startHere, description: value })}
            placeholder="Help visitors pick a first product."
          />
        </Field>

        <div className="space-y-4">
          {/* Keys below are indexes ON PURPOSE: ids are editable, and a key
              derived from the value being edited remounts the card on every
              keystroke (focus loss). All inputs are controlled and there is
              no reordering, so index keys are safe here. */}
          {startHere.questions.map((question, questionIndex) => (
            <div key={questionIndex} className="leadline-border-thin bg-white p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="Question ID">
                  <TextInput
                    value={question.id}
                    onChange={(value) => updateQuestion(questionIndex, { ...question, id: value })}
                    placeholder="question-id"
                  />
                </Field>
                <Field label="Question Prompt">
                  <TextInput
                    value={question.prompt}
                    onChange={(value) => updateQuestion(questionIndex, { ...question, prompt: value })}
                    placeholder="What is your top priority?"
                  />
                </Field>
              </div>

              <div className="space-y-2">
                {question.options.map((option, optionIndex) => (
                  <div key={optionIndex} className="leadline-border-thin bg-[#F9F5EE] p-2 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Field label="Option ID">
                        <TextInput
                          value={option.id}
                          onChange={(value) => updateOption(questionIndex, optionIndex, { ...option, id: value })}
                          placeholder="option-id"
                        />
                      </Field>
                      <Field label="Option Label">
                        <TextInput
                          value={option.label}
                          onChange={(value) => updateOption(questionIndex, optionIndex, { ...option, label: value })}
                          placeholder="Ship this week"
                        />
                      </Field>
                    </div>

                    <div className="space-y-1">
                      {option.weights.map((weight, weightIndex) => (
                        <div key={weightIndex} className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-7">
                            <Field label="Product">
                              <select
                                value={weight.productId}
                                onChange={(e) =>
                                  updateWeight(questionIndex, optionIndex, weightIndex, {
                                    ...weight,
                                    productId: e.target.value,
                                  })
                                }
                                className="w-full leadline-border-thin px-2 py-1 bg-white text-sm"
                              >
                                <option value="">Select product</option>
                                {(products || []).map((product) => (
                                  <option key={product.id} value={product.id}>
                                    {product.name}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          </div>
                          <div className="col-span-3">
                            <Field label="Score">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={weight.score}
                                onChange={(e) =>
                                  updateWeight(questionIndex, optionIndex, weightIndex, {
                                    ...weight,
                                    score: Number(e.target.value),
                                  })
                                }
                                className="w-full leadline-border-thin px-2 py-1 bg-white text-sm"
                              />
                            </Field>
                          </div>
                          <div className="col-span-2">
                            <button
                              onClick={() => deleteWeight(questionIndex, optionIndex, weightIndex)}
                              className="w-full text-xs text-red-600 hover:underline pb-1"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => addWeight(questionIndex, optionIndex)}
                        className="leadline-border-thin px-2 py-1 text-xs bg-white hover:bg-[#F5EDE0]"
                      >
                        + Add weight
                      </button>
                      <button
                        onClick={() => deleteOption(questionIndex, optionIndex)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Delete option
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => addOption(questionIndex)}
                  className="leadline-border-thin px-2 py-1 text-xs bg-white hover:bg-[#F5EDE0]"
                >
                  + Add option
                </button>
                <button onClick={() => deleteQuestion(questionIndex)} className="text-xs text-red-600 hover:underline">
                  Delete question
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={addQuestion}
          className="leadline-border-thin px-3 py-1 text-xs bg-white text-[#3A3530] hover:bg-[#F5EDE0]"
        >
          + Add question
        </button>

        {errors.length > 0 ? (
          <div className="space-y-1">
            {errors.map((error) => (
              <p key={error} className="text-xs text-red-600">
                {error}
              </p>
            ))}
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
}

export default StartHereTab;
